import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import axios from "axios";
import { enqueueMutation } from "../lib/offlineCache";
import { toast } from "sonner";
import {
  Sun,
  ChevronRight,
  CheckCircle,
  Flame,
  Award,
  Shield,
  Heart,
  Sparkles,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step 1 — overall feeling. Calm 5-option scale, all gentle. Maps to a
// traffic-light signal for the instructor brief: green / amber / red.
//
// Principle the user wrote into the spec:
//   "No negative option without a positive counterpart."
//   "If a learner had a hard lesson but corrected mistakes, the system
//    should frame that as progress, not failure."
const OVERALL_FEELINGS = [
  { id: "great",      label: "Great",      hint: "Confident and ready",        traffic: "green", weight: 100 },
  { id: "good",       label: "Good",       hint: "Calm and focused",           traffic: "green", weight: 80 },
  { id: "steady",     label: "Steady",     hint: "Showing up, doing the work", traffic: "green", weight: 60 },
  { id: "unsure",     label: "Unsure",     hint: "Confidence still building",  traffic: "amber", weight: 40 },
  { id: "struggling", label: "Struggling", hint: "I need a little support",    traffic: "red",   weight: 15 },
];

// Positive states surfaced FIRST regardless of overall feeling. Includes
// the user's required "corrected mistakes = progress" framing.
const POSITIVE_STATES = [
  { id: "proud",             label: "Proud of last lesson",   hint: "I corrected something I'd been getting wrong" },
  { id: "made_progress",     label: "Made progress",          hint: "Something clicked for me" },
  { id: "motivated",         label: "Motivated",              hint: "I want to push through the next step" },
  { id: "focused",           label: "Focused",                hint: "Mind is clear, ready to learn" },
  { id: "excited_next",      label: "Excited for next lesson" },
  { id: "thinking_ahead",    label: "Thinking ahead",         hint: "Planning my route mentally" },
  { id: "feeling_calm",      label: "Calm",                   hint: "Grounded and centred" },
];

// Support states — only shown if learner picks Unsure or Struggling.
// Wording is gentle: "Mind is busy" (not "overthinking"), "Confidence dip"
// (not "low confidence"), "Hasn't clicked yet" (not "frustration").
const SUPPORT_STATES = [
  { id: "mind_busy",         label: "Mind is busy",           hint: "Lots of thoughts about driving — let's slow down" },
  { id: "confidence_dip",    label: "Confidence dip",         hint: "Just need a confidence boost today" },
  { id: "nervous",           label: "A bit nervous",          hint: "Slight butterflies before the lesson" },
  { id: "test_pressure",     label: "Test on my mind",        hint: "The test is looming" },
  { id: "didnt_click_yet",   label: "Hasn't clicked yet",     hint: "Something I'm working on isn't there yet" },
  { id: "want_reassurance",  label: "Want reassurance",       hint: "I just want to know I'm doing okay" },
  { id: "specific_situation", label: "A specific road or situation", hint: "There's something I'd like to ease into" },
];

// Legacy MINDSET_OPTIONS kept for backwards-compatible API payload (the
// backend's mindset_states array still accepts the old IDs and the new
// ones live alongside; the new structure simply maps both ways below).
const MINDSET_OPTIONS = [...POSITIVE_STATES, ...SUPPORT_STATES];

const REFLECTIONS = [
  "Lighter — I feel calmer",
  "Stronger — I see something I can build on",
  "Clearer — I know what to focus on",
  "Exactly what I needed right now",
];

const STAGE_ICONS = { 1: Sun, 2: Shield, 3: Award };

export default function DailyCheckIn() {
  const navigate = useNavigate();
  // step: intro → overall → positives → support (optional) → guidance → reflection → complete
  const [step, setStep] = useState("intro");
  const [overall, setOverall] = useState(null);          // {id, label, traffic, weight}
  const [selectedStates, setSelectedStates] = useState([]);  // multi-select positive + support
  const [guidance, setGuidance] = useState(null);
  const [loadingGuidance, setLoadingGuidance] = useState(false);
  const [reflection, setReflection] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState("");

  const toggleState = (id) => {
    setSelectedStates((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const fetchGuidance = async () => {
    setLoadingGuidance(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/checkin/mindset-guidance`,
        {
          mindset_states: selectedStates,
          overall: overall?.id,
          overall_traffic: overall?.traffic,
        },
        { withCredentials: true }
      );
      setGuidance(data);
    } catch {
      // Soft fallback — still move forward with a gentle default.
      setGuidance({
        umbrella: overall?.traffic === "red"
          ? "You showed up. That's the most important step."
          : "You're doing the work. That matters.",
        tone: overall?.traffic === "red" ? "heavy" : overall?.traffic === "amber" ? "mixed" : "calm",
        blocks: [],
        closing: "Slow is control.",
      });
    } finally {
      setLoadingGuidance(false);
      setStep("guidance");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = {
      mindset_states: selectedStates,
      reflection_answer: reflection,
      overall: overall?.id,
      overall_traffic: overall?.traffic,
      overall_weight: overall?.weight,
    };

    // If we're offline, queue the check-in and keep the user moving.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await enqueueMutation({
        url: "/api/checkin",
        method: "POST",
        body: payload,
      });
      toast.success("Saved — we'll send this the moment you're back online.", { duration: 4000 });
      setResult(null);
      setStep("complete");
      setSubmitting(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/api/checkin`,
        payload,
        { withCredentials: true }
      );
      setResult(data);
      setStep("complete");
    } catch {
      // Network hiccup mid-submit — still preserve the user's work.
      await enqueueMutation({
        url: "/api/checkin",
        method: "POST",
        body: payload,
      });
      toast.success("Network blip — your check-in is saved and will sync shortly.", { duration: 4000 });
      setStep("complete");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct =
    step === "overall" ? 25 :
    step === "positives" ? 50 :
    step === "support" ? 65 :
    step === "guidance" ? 80 :
    step === "reflection" ? 95 :
    step === "complete" ? 100 : 10;

  // ---------- INTRO ----------
  if (step === "intro") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm text-center space-y-8">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto">
              <Sun className="w-9 h-9 text-white" />
            </div>

            <div>
              <h1 data-testid="checkin-title" className="text-2xl font-black text-slate-900 mb-4">
                Driving Mindset Check-In
              </h1>
              <div className="text-left bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <p className="text-slate-700 font-medium">Before we drive, we check in with you.</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  How you arrive at the car decides how the lesson goes.<br />
                  Tell us what's on your mind — we'll help you settle before you turn the key.
                </p>
                <p className="text-slate-400 text-xs italic">No wrong answers. This is just for you.</p>
              </div>
            </div>

            <Button
              data-testid="checkin-start-btn"
              onClick={() => setStep("overall")}
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base"
            >
              Begin Check-In
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>

            <button
              data-testid="skip-checkin-btn"
              onClick={() => navigate("/dashboard")}
              className="text-sm text-slate-400 hover:text-slate-600"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- STEP 1 · OVERALL FEELING (5-point scale) ----------
  if (step === "overall") {
    const trafficStyle = (t) =>
      t === "green" ? "border-emerald-300 bg-emerald-50 hover:border-emerald-400" :
      t === "amber" ? "border-amber-300 bg-amber-50 hover:border-amber-400" :
                      "border-rose-300 bg-rose-50 hover:border-rose-400";
    const trafficActive = (t) =>
      t === "green" ? "ring-emerald-500" :
      t === "amber" ? "ring-amber-500" : "ring-rose-500";
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="max-w-sm mx-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500 font-medium">Step 1 of 2</p>
              <p className="text-sm font-semibold text-amber-600">{progressPct}%</p>
            </div>
            <Progress value={progressPct} className="h-2 bg-slate-100" />
          </div>
        </header>

        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-sm">
            <h2 data-testid="overall-title" className="text-xl font-black text-slate-900 mb-2 leading-tight">
              How are you feeling about driving today?
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              One quick tap. Whatever you pick is the right answer.
            </p>

            <div data-testid="overall-options" className="space-y-2.5">
              {OVERALL_FEELINGS.map((opt) => {
                const selected = overall?.id === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`overall-option-${opt.id}`}
                    data-traffic={opt.traffic}
                    onClick={() => setOverall(opt)}
                    aria-pressed={selected}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${trafficStyle(opt.traffic)} ${selected ? `ring-2 ring-offset-2 ${trafficActive(opt.traffic)}` : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        opt.traffic === "green" ? "bg-emerald-500" :
                        opt.traffic === "amber" ? "bg-amber-500" : "bg-rose-500"
                      }`} />
                      <div className="flex-1">
                        <p className="text-base font-bold text-slate-900">{opt.label}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{opt.hint}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-3">
              <Button
                data-testid="overall-continue-btn"
                onClick={() => {
                  // Routing rule:
                  //  green  → straight to positives (no support form needed)
                  //  amber/red → positives THEN support
                  setStep("positives");
                }}
                disabled={!overall}
                className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base disabled:opacity-40"
              >
                {overall ? "Continue" : "Pick one"}
                {overall && <ChevronRight className="w-5 h-5 ml-1" />}
              </Button>
              <button
                data-testid="skip-overall-btn"
                onClick={() => navigate("/dashboard")}
                className="w-full text-xs text-slate-400 hover:text-slate-600 text-center"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- STEP 2a · POSITIVE STATES (always shown, surfaces strengths first) ----------
  if (step === "positives") {
    const needsSupport = overall?.traffic === "amber" || overall?.traffic === "red";
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="max-w-sm mx-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500 font-medium">
                {needsSupport ? "Step 2 of 2 · Strengths" : "Step 2 of 2"}
              </p>
              <p className="text-sm font-semibold text-amber-600">{progressPct}%</p>
            </div>
            <Progress value={progressPct} className="h-2 bg-slate-100" />
          </div>
        </header>

        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-sm">
            <h2 data-testid="positives-title" className="text-xl font-black text-slate-900 mb-2 leading-tight">
              Anything going well today?
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Tick what feels true. Even small wins count — corrected mistakes <em>are</em> progress.
            </p>

            <div data-testid="positives-options" className="grid grid-cols-1 gap-2.5">
              {POSITIVE_STATES.map((opt) => {
                const selected = selectedStates.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    data-testid={`positive-option-${opt.id}`}
                    onClick={() => toggleState(opt.id)}
                    aria-pressed={selected}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                      selected ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? "border-emerald-500 bg-emerald-500" : "border-slate-300"
                      }`}>
                        {selected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${selected ? "text-emerald-800" : "text-slate-800"}`}>{opt.label}</p>
                        {opt.hint && <p className="text-xs text-slate-500 mt-0.5">{opt.hint}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-3">
              <Button
                data-testid="positives-continue-btn"
                onClick={() => {
                  if (needsSupport) setStep("support");
                  else fetchGuidance();
                }}
                disabled={loadingGuidance}
                className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base disabled:opacity-40"
              >
                {loadingGuidance ? "Listening..." : needsSupport ? "What would help" : "See what would help"}
                {!loadingGuidance && <ChevronRight className="w-5 h-5 ml-1" />}
              </Button>
              <button
                data-testid="back-to-overall-btn"
                onClick={() => setStep("overall")}
                className="w-full text-xs text-slate-400 hover:text-slate-600 text-center"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- STEP 2b · SUPPORT STATES (only if Unsure or Struggling) ----------
  if (step === "support") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="max-w-sm mx-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500 font-medium">Step 2 of 2 · Support</p>
              <p className="text-sm font-semibold text-amber-600">{progressPct}%</p>
            </div>
            <Progress value={progressPct} className="h-2 bg-slate-100" />
          </div>
        </header>

        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-sm">
            <h2 data-testid="support-title" className="text-xl font-black text-slate-900 mb-2 leading-tight">
              What's weighing on you?
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              No wrong answers. Knowing helps your instructor steer the lesson gently.
            </p>

            <div data-testid="support-options" className="grid grid-cols-1 gap-2.5">
              {SUPPORT_STATES.map((opt) => {
                const selected = selectedStates.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    data-testid={`support-option-${opt.id}`}
                    onClick={() => toggleState(opt.id)}
                    aria-pressed={selected}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                      selected ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? "border-amber-500 bg-amber-500" : "border-slate-300"
                      }`}>
                        {selected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${selected ? "text-amber-800" : "text-slate-800"}`}>{opt.label}</p>
                        {opt.hint && <p className="text-xs text-slate-500 mt-0.5">{opt.hint}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-3">
              <Button
                data-testid="support-continue-btn"
                onClick={fetchGuidance}
                disabled={loadingGuidance}
                className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base disabled:opacity-40"
              >
                {loadingGuidance ? "Listening..." : "See what would help"}
                {!loadingGuidance && <ChevronRight className="w-5 h-5 ml-1" />}
              </Button>
              <button
                data-testid="back-to-positives-btn"
                onClick={() => setStep("positives")}
                className="w-full text-xs text-slate-400 hover:text-slate-600 text-center"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Legacy `mindset` step alias — keeps any deep-links / old onboarding flows
  // that pointed to this step working. Routes them through the new flow.
  if (step === "mindset") {
    setStep("overall");
    return null;
  }

  // ---------- PERSONALISED GUIDANCE ----------
  if (step === "guidance") {
    const g = guidance || { umbrella: "", blocks: [], closing: "" };
    const toneColor =
      g.tone === "calm" ? "emerald" :
      g.tone === "heavy" ? "rose" :
      g.tone === "mixed" ? "amber" : "slate";
    const iconBg = {
      emerald: "bg-emerald-100 text-emerald-700",
      rose:    "bg-rose-100 text-rose-700",
      amber:   "bg-amber-100 text-amber-700",
      slate:   "bg-slate-100 text-slate-700",
    }[toneColor];

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="max-w-sm mx-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500 font-medium">Your support for today</p>
              <p className="text-sm font-semibold text-amber-600">{progressPct}%</p>
            </div>
            <Progress value={progressPct} className="h-2 bg-slate-100" />
          </div>
        </header>

        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center space-y-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto ${iconBg}`}>
                <Heart className="w-7 h-7" />
              </div>
              <h2 data-testid="guidance-umbrella" className="text-xl font-black text-slate-900 leading-snug">
                {g.umbrella}
              </h2>
            </div>

            <div data-testid="guidance-blocks" className="space-y-3">
              {g.blocks.map((b) => (
                <div
                  key={b.state}
                  data-testid={`guidance-block-${b.state}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5"
                >
                  <p className="font-bold text-slate-900 text-sm leading-snug">{b.title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.body}</p>
                  <div className="flex items-start gap-2 pt-2 border-t border-slate-100">
                    <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-700 leading-relaxed">
                      <span className="font-semibold text-slate-900">Try this: </span>{b.tool}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {g.closing && (
              <div data-testid="guidance-closing" className="text-center text-sm text-slate-600 italic px-4">
                {g.closing}
              </div>
            )}

            <Button
              data-testid="guidance-continue-btn"
              onClick={() => setStep("reflection")}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl text-base"
            >
              I'm ready
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>

            <button
              data-testid="guidance-back-btn"
              onClick={() => setStep("mindset")}
              className="w-full text-xs text-slate-400 hover:text-slate-600 text-center"
            >
              Change my answers
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- REFLECTION ----------
  if (step === "reflection") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm space-y-8">
            <div className="text-center">
              <h2 data-testid="reflection-title" className="text-2xl font-black text-slate-900 mb-2">
                How do you feel now?
              </h2>
            </div>

            <div className="space-y-3">
              {REFLECTIONS.map((opt) => {
                const selected = reflection === opt;
                return (
                  <button
                    key={opt}
                    data-testid={`reflection-${REFLECTIONS.indexOf(opt)}`}
                    onClick={() => setReflection(opt)}
                    className={`w-full p-4 rounded-2xl border-2 text-left font-medium transition-all ${
                      selected
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                  >
                    <span className="text-sm">{opt}</span>
                  </button>
                );
              })}
            </div>

            <Button
              data-testid="finish-checkin-btn"
              onClick={handleSubmit}
              disabled={!reflection || submitting}
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base disabled:opacity-40"
            >
              {submitting ? "Saving..." : "Finish Check-In"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- COMPLETION ----------
  if (step === "complete") {
    const stage = result?.stage;
    const nextStage = result?.next_stage;
    const stageProgress = stage && nextStage
      ? Math.round(((result.total_points - stage.min) / (nextStage.min - stage.min)) * 100)
      : stage?.stage === 3 ? 100 : 0;
    const StageIcon = stage ? (STAGE_ICONS[stage.stage] || Sun) : Sun;

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
              <CheckCircle className="w-9 h-9 text-emerald-600" />
            </div>

            <div>
              <h2 data-testid="complete-title" className="text-2xl font-black text-slate-900 mb-3">
                You showed up. That counts.
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed max-w-xs mx-auto">
                Little and often builds the driver you want to be.
              </p>
            </div>

            <div className="space-y-3">
              <div data-testid="points-earned" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-amber-700">+{result?.points_awarded || 0}</span>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-amber-900 text-sm">Points earned</p>
                  <p className="text-xs text-amber-600">Total: {result?.total_points || 0} points</p>
                </div>
              </div>

              <div data-testid="streak-count" className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-orange-700" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-orange-900 text-sm">{result?.streak || 0} day streak</p>
                  <p className="text-xs text-orange-600">Keep it going!</p>
                </div>
              </div>

              {stage && (
                <div data-testid="stage-progress" className="bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <StageIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-900 text-sm">Stage {stage.stage}: {stage.name}</p>
                      <p className="text-xs text-slate-500">{stage.desc}</p>
                    </div>
                  </div>
                  {nextStage && (
                    <div>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                        <span>{result?.total_points || 0} pts</span>
                        <span>{nextStage.min} pts to {nextStage.name}</span>
                      </div>
                      <Progress value={stageProgress} className="h-2 bg-slate-100" />
                    </div>
                  )}
                  {stage.stage === 3 && (
                    <p className="text-xs text-emerald-600 font-medium mt-2">Maximum stage reached!</p>
                  )}
                </div>
              )}

              {result?.new_achievement && (
                <div data-testid="new-achievement" className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 text-white text-center">
                  <Award className="w-10 h-10 mx-auto mb-2" />
                  <p className="font-bold text-lg">Driver Control Certificate</p>
                  <p className="text-amber-100 text-xs mt-1 leading-relaxed">
                    You have demonstrated awareness, control, and consistency in your driving mindset.
                  </p>
                </div>
              )}
            </div>

            <Button
              data-testid="continue-to-support-btn"
              onClick={() => setStep("support_level")}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-base"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- SUPPORT LEVEL ----------
  if (step === "support_level") {
    const SUPPORT_OPTIONS = [
      { id: "normal_challenge", label: "Push me today", desc: "I'm up for being stretched." },
      { id: "steady_progress", label: "Steady — building confidence", desc: "I want to go at a calm pace and lock things in." },
      { id: "extra_support", label: "Go gentle — I need support", desc: "A bit more time, patience, and clear steps today." },
    ];

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm space-y-6">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-2">Before your lesson</p>
              <h2 data-testid="support-level-title" className="text-2xl font-black text-slate-900 mb-2">
                How do you want today to feel?
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                Pick the level that'll help you get the most out of today.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Your instructor will shape the lesson around this.
              </p>
            </div>

            <div className="space-y-2.5">
              {SUPPORT_OPTIONS.map((opt) => {
                const isSelected = selectedSupport === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`support-${opt.id}`}
                    onClick={() => setSelectedSupport(opt.id)}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                      isSelected
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <p className={`font-medium text-sm ${isSelected ? "text-slate-900" : "text-slate-700"}`}>{opt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                );
              })}
            </div>

            <Button
              data-testid="confirm-support-btn"
              disabled={!selectedSupport || submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  await axios.post(`${API_URL}/api/support-level`, { level: selectedSupport }, { withCredentials: true });
                } catch {}
                setSubmitting(false);
                navigate("/dashboard");
              }}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl text-base disabled:opacity-40"
            >
              {submitting ? "Saving..." : "Start my lesson"}
              {!submitting && <ChevronRight className="w-5 h-5 ml-1" />}
            </Button>

            <button
              data-testid="skip-support-btn"
              onClick={() => navigate("/dashboard")}
              className="w-full text-xs text-slate-400 hover:text-slate-600 text-center"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
